import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../components/ui/input-group";
import { useAuth } from "../hooks/useAuth";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTypewriter } from "../hooks/useTypewriter";

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [query, setQuery] = useState("");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const endings = t("home.hero.endings", { returnObjects: true });
  const typed = useTypewriter(Array.isArray(endings) ? endings : [], { animate: !reduceMotion });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const features = [
    {
      key: "trackProgress",
      title: t("home.features.trackProgress.title"),
      description: t("home.features.trackProgress.description"),
    },
    {
      key: "discoverRoutes",
      title: t("home.features.discoverRoutes.title"),
      description: t("home.features.discoverRoutes.description"),
    },
    {
      key: "community",
      title: t("home.features.community.title"),
      description: t("home.features.community.description"),
    },
    {
      key: "offlineAccess",
      title: t("home.features.offlineAccess.title"),
      description: t("home.features.offlineAccess.description"),
    },
    {
      key: "trainingPlans",
      title: t("home.features.trainingPlans.title"),
      description: t("home.features.trainingPlans.description"),
    },
    {
      key: "multiDevice",
      title: t("home.features.multiDevice.title"),
      description: t("home.features.multiDevice.description"),
    },
  ];

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    navigate({ pathname: "/search", search: q ? `?${new URLSearchParams({ q })}` : "" });
  };

  return (
    <div className="flex flex-col items-center justify-center gap-6 text-center">
      <h1 className="text-base font-semibold text-primary">{t("home.welcome")}</h1>
      {/* The line types itself: decoration, so a screen reader is not read every keystroke. */}
      <p
        aria-hidden="true"
        className="min-h-[2lh] text-4xl font-bold tracking-tight md:text-5xl lg:min-h-0"
      >
        {t("home.hero.prefix")} <span className="text-primary">{typed}</span>
        <span className="ml-1 inline-block h-[0.9em] w-[3px] translate-y-[0.1em] bg-primary animate-caret-blink motion-reduce:animate-none" />
      </p>

      <form role="search" onSubmit={handleSearch} className="mt-4 flex w-full max-w-xl gap-2">
        <InputGroup className="h-12">
          <InputGroupAddon>
            <Search className="h-4 w-4" />
          </InputGroupAddon>
          <InputGroupInput
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            aria-label={t("search.placeholder")}
            className="text-base"
          />
        </InputGroup>
        <Button type="submit" className="h-12">
          {t("home.search")}
        </Button>
      </form>
      <p className="text-lg text-muted-foreground max-w-[600px]">{t("home.description")}</p>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 w-full max-w-5xl mt-8">
        {features.map((feature) => (
          <div
            key={feature.key}
            className="flex flex-col gap-2 p-6 border rounded-lg bg-card text-card-foreground shadow-sm"
          >
            <h3 className="text-lg font-semibold">{feature.title}</h3>
            <p className="text-muted-foreground">{feature.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
